import groq from 'groq'

import appConfig from '~/config/app'

// Single source of truth for the "is this learning material an article rather than a video?" flag.
// Shared so the lightweight link-preview query (curriculumSubtopicTitles) can't drift from the
// full projection below.
export const curriculumIsArticleProjection = groq`
  "isArticle": defined(htmlContent) || count(videoContent[_type=='awsVideo']) == 0
`

// Shared projection for an article/video learning material (a curriculumContent's non-activity
// learningMaterials). Reused by both the live sub-topic query (curriculumSubtopicList) and the
// standalone preview query (curriculumStandaloneContent) so the two surfaces render identical content.
// The "isArticle" flag is load-bearing: MasteryTrackContent branches on it.
export const curriculumLearningMaterialProjection = groq`
  key,
  progressId,
  title,
  ${curriculumIsArticleProjection},
  videoContent,
  difficulties,
  prompt,
  _type,
  _id,
  cssUrl,
  h1,
  htmlContent
`

// Fetches a single curriculumContent doc standalone, by _id alone — no grade/topic/subtopic
// context required or returned. Shared by the CMS preview surface (src/preview) and the
// public standalone-content page (src/standalone-content) so both render identical content.
// Note: the activities block is projected inline (not shared) — unlike curriculumSubtopicList, a
// standalone curriculumContent has no enclosing competencyGoal scope, so the live query's
// "level": ^.^.level is intentionally omitted here.
export const curriculumStandaloneContent = groq`
  *[_type == "curriculumContent" && _id == $id][0]{
    "id": _id,
    baseId,
    basePath,
    competencyLevel,
    curriculumPath[]->,
    learningMaterials[@->_type!="curriculumActivityType"]->{ ${curriculumLearningMaterialProjection} },
    "activities": learningMaterials[@->_type=="curriculumActivityType"]->{
      _type,
      _id,
      key,
      title,
      difficulties[]{
        ...,
        "activityName": activityName->name,
        "keyboardLayout": keyboardLayout->layoutName
      },
    },
    metaDescription,
    slug,
    title,
    usefulWordsDictionary->,
  }`

// Grade overview query: fetches full grade with all topics (subjectOrders) from curriculumGradeV2.
// Params: { id: string } — e.g. id: 'grade-us-5'
// Entities in subjectOrders are topics.
export const curriculumGradeOverview = groq`
  *[_type == "curriculumGradeV2" && _id == $id]{
    _id,
    countryCode,
    gradeLevel,
    title,
    gradeSubdivisions[]-> {
      _id,
      _type,
      level,
      label,
      icon,
      countryCode,
      gradeLevel,
      subjectOrders[]-> {
        _id,
        _type,
        order,
        subject,
        subjectLabel,
        icon,
        subdivisionLevel,
        competencyGoals[]-> {
          _id,
          level,
          label,
          description,
          subject,
          topicId,
          subjectOrderNumber,
          "levelCount": count(contents),
        }
      }
    }
  }`

export const curriculumActivitiesGroq = groq`*[_type=="curriculumContent" && _id == $_id][0] {
    _id,
    title,
    "activities": learningMaterials[@->_type=="curriculumActivityType"]->{
        _type,
        key,
        title,
        difficulties[] {
            ...,
            "activityName": activityName->name,
            "keyboardLayout": keyboardLayout->layoutName
        },
    }
}`

export const getSubjectInfoForMappingQuiz = groq`
  *[_type=="curriculumGrade" && _id == $gradeId]{
    subjectOrder[order==$subjectOrder][0],
    competencyGoals[subjectOrder==$subjectOrder]
  }
  {
    "subjectLabel":subjectOrder.subject,
    "subjectOrder": subjectOrder.order,
    "subjectOrderLabel": subjectOrder.subjectLabel,
    "icon":subjectOrder.icon,
    competencyGoals
  }[0]
`

export const getTopicInfoForMappingQuiz = groq`
*[_type == "curriculumGradeV2" && _id==$gradeId][0]{
  gradeLabel,
  gradeLevel,
  "topics": gradeSubdivisions[]->.subjectOrders[(@->order==$topic)]->{
    "subjectLabel": subject,
    "subjectOrder": order,
    "subjectOrderLabel": subjectLabel,
    icon,
    "topicId": competencyGoals[0]->{
      topicId
    }.topicId,
    "subdivisionOrder": competencyGoals[0]->{
      subdivisionLevel
    }.subdivisionLevel,
    competencyGoals[]->{
      _id,
      level,
      label,
      description,
      subject,
      topicId,
      subjectOrderNumber,
      "levelCount": count(contents),
      "isCompleted":false,
      "path": "/curriculum/v2/"+string(^.^.gradeLevel)+"/"+string(^.order)+"/"+string(level),
      "levels":contents[]-> {
          "id":_id,
          baseId,
          basePath,
          competencyLevel,
          curriculumPath[]->,
          learningMaterials[@->_type!="curriculumActivityType"]->,
          "activities": learningMaterials[@->_type=="curriculumActivityType"]->{
              _type,
              _id,
              key,
              title,
              difficulties[] {
                  ...,
                  "activityName": activityName->name,
                  "keyboardLayout": keyboardLayout->layoutName
              },
          },
          metaDescription,
          slug,
          title,
          usefulWordsDictionary->,
      }
    }
  }
}{
  "topic":topics[0]
}`
export const curriculumGradesListV1 = groq`*[_type == "curriculumGrade"
        && countryCode == $countryCode] | order(gradeLevel asc)`

export const curriculumGradesList = groq`
    *[_type == "curriculumGradeV2" && countryCode==$countryCode && !(_id in path("drafts.**"))]
    {
      _id,
      countryCode,
      gradeLabel,
      gradeLevel,
      title,
      "hasSubDivision": count(gradeSubdivisions) > 1,
      isSplittedGrade,
      "subjectCount": count(
        gradeSubdivisions[]->.subjectOrders[]._ref
      ),
      "firstSubdivision":gradeSubdivisions[0]->.level,
      "firstTopicOrder":gradeSubdivisions[0]->.subjectOrders[0]->.order,
      "isJuniorMathGrade": gradeLevel < 5
    } | order(gradeLevel asc)`

// Grade options for the school admin class form: the grades available for one curriculum
// region, lightest possible projection.
export const schoolClassGradeOptions = groq`*[_type == "curriculumGradeV2" && countryCode==$countryCode]{
  "gradeId": _id,
  gradeLabel,
  gradeLevel
} | order(gradeLevel asc)`

export const curriculumGradeTitles = groq`*[_type == "curriculumGradeV2" && _id in $ids]{"id":_id, "title":gradeLabel}`

// A whole curriculum's grade labels, keyed by gradeLevel instead of gradeId, so callers holding only a
// level (e.g. a stored certificate) can resolve it. v2 only: it covers every countryCode + gradeLevel
// pair v1 has, and the two disagree where v1 kept the pre-rename US labels ("11th grade" vs "Algebra 2").
export const curriculumGradeLabels = groq`*[_type == "curriculumGradeV2"
    && countryCode == $countryCode && !(_id in path("drafts.**"))]{gradeLevel, gradeLabel}`

// Lightweight counterpart to curriculumSubtopicList: same traversal, but dereferences only the
// content matching $slug and keeps titles + the article/video flag — no htmlContent/videoContent,
// since the Oracle link preview fetches this client-side per link. `description` is the fallback
// title for URLs carrying no level slug.
export const curriculumSubtopicTitles = groq`
    *[_type == "curriculumGradeV2" && _id == $id][0]{
    gradeLevel,
    "subDivisions": gradeSubdivisions[]-> {
      "topics": subjectOrders[(@->order==$order)]-> {
        "competencyGoals": competencyGoals[(@->level==$subtopic)]-> {
          description,
          contents[@->slug==$slug]->{
            title,
            "learningMaterials": learningMaterials[@->_type!="curriculumActivityType"]->{ ${curriculumIsArticleProjection} }
          }
        }
      }
    }
  }`

export const curriculumSubtopicList = groq`
    *[_type == "curriculumGradeV2" && _id == $id]{
    _id,
    countryCode,
    gradeLevel,
    title,
    gradeSubdivisions[]-> {
      _id,
      _type,
      level,
      label,
      icon,
      countryCode,
      gradeLevel,
      subjectOrders[(@->order==$order)]-> {
        _id,
        _type,
        order,
        subject,
        subjectLabel,
        icon,
        subdivisionLevel,
        "isComplete": false,
        competencyGoals[(@->level==$subtopic)]-> {
          _id,
          level,
          label,
          description,
          subject,
          topicId,
          subjectOrderNumber,
          "levelCount": count(contents),
          contents[]-> {
                "id":_id,
                baseId,
                basePath,
                competencyLevel,
                curriculumPath[]->,
                learningMaterials[@->_type!="curriculumActivityType"]->{ ${curriculumLearningMaterialProjection} },
                "activities": learningMaterials[@->_type=="curriculumActivityType"]->{
                    _type,
                    _id,
                    key,
                    title,
                    difficulties[] {
                        ...,
                        "level":^.^.level,
                        "activityName": activityName->name,
                        "keyboardLayout": keyboardLayout->layoutName
                    },
                },
                metaDescription,
                slug,
                title,
                usefulWordsDictionary->,
            }
        }
      }
    }
  }`

export const curriculumGradeSubjectList = (): string => {
  return groq`{
        "gradeDetails":  *[_type == "curriculumGrade" && _id == $gradeId ][0],
        "bootcampLevels": *[_type == "bootcampModule" && (references($gradeId) || count(pages[]-> [ references($gradeId) ]) > 0) 
          && module-> language == $language && isPublished] {    
          "activityLevels": pages[
            @->isHidden != true && 
            @->curriculumGrade[level._ref == $gradeId][0].level._ref == $gradeId
            ]->{
                "competency": *[_type == "curriculumGrade" && _id ==$gradeId]{
                    ...competencyGoals[level == ^.^.curriculumGrade[level._ref == $gradeId][0].competencyGoals ][0]
                }[0],
              } | order(level asc)
        },
        "mathVaultLevels": *[_type=="juniorDrillTabs" && references($gradeId)] {   
          "competencies" : curriculumGrade[level._ref == $gradeId]{        
              "title": levelTitle,
              "competencyGoalCode" : competencyGoals,
              ...(*[_type == "curriculumGrade" && _id == $gradeId ] {
                ...(competencyGoals[level == ^.^.competencyGoals]){
                    subject,
                }[0]
              }[0]),
          },
        },
      }`
}

export const curriculumGradeContent = (): string => {
  const isPublished = appConfig.isProdEnvironment ? '&& isPublished' : ''
  return groq`{
                "gradeDetails":  *[_type == "curriculumGrade" && _id == $gradeId ][0],
                "bootcampLevels": *[_type == "bootcampModule" && (references($gradeId) || count(pages[]-> [ references($gradeId) ]) > 0) && module-> language == $language ${isPublished}] {
                    "hasWhyLevel": hasWhyLevel == true,
                    "bootcampId" : _id,
                    "bootcampModule": {
                        "allActivityLevels": pages[@->isHidden != true]->{
                            level,
                            "videos": count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]),
                            "rewards" : activities[]->{
                                "activitySet": type,
                                "difficultyLevelsCount": math::sum(difficulties[].rewards)
                            } | order(activitySet asc)
                        }
                    },
                    "whyLevel": select( hasWhyLevel == true => 
                        {
                            "type" : $bootcampType,
                            "level" : 1,
                            "hasEncyclopedia":defined(encyclopediaArticle),
                            "title": whyName,
                            "path": curriculumGrade[level._ref == $gradeId][0].path,
                            "videos": count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]),
                            "competency": *[_type == "curriculumGrade" && _id == $gradeId][0].competencyGoals[
                                level in ^.curriculumGrade[level._ref == $gradeId].competencyGoals
                            ]{
                                ...,
                                "levelOrder": select(
                                defined(^.curriculumGrade[level._ref == $gradeId][competencyGoals == ^.level][0].levelOrder) => ^.curriculumGrade[level._ref == $gradeId][competencyGoals == ^.level][0].levelOrder,
                                null
                                )
                            }
                        }
                    ),
                    "activityLevels": pages[
                        @->isHidden != true && 
                        @->curriculumGrade[level._ref == $gradeId][0].level._ref == $gradeId
                        ]->{
                            "type": $bootcampType,
                            level,
                            "hasEncyclopedia": defined(encyclopediaArticle) || count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]) == 0,
                            "path": curriculumGrade[level._ref == $gradeId][0].path,
                            "rewards": activities[]->{ "sum": math::sum(difficulties[].rewards) },
                            "title": coalesce(name, shortTitle),
                            "videos": count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]),
                            "activities" : activities[]->{
                                "activitySet": type,
                                "difficultyLevels":difficulties[]{
                                    rewards
                                },
                                "difficultyLevelsCount": math::sum(difficulties[].rewards),
                            },
                            "competency": *[_type == "curriculumGrade" && _id == $gradeId][0].competencyGoals[
                                level in ^.curriculumGrade[level._ref == $gradeId].competencyGoals
                            ]{
                                ...,
                                "levelOrder": select(
                                defined(^.curriculumGrade[level._ref == $gradeId][competencyGoals == ^.level][0].levelOrder) => ^.curriculumGrade[level._ref == $gradeId][competencyGoals == ^.level][0].levelOrder,
                                null
                                )
                            } 
                        } | order(level asc)
            },
            "mathVaultLevels": *[_type=="juniorDrillTabs" && references($gradeId)]
                {
                    "type" : $mathVaultType,
                    "details" : {
                        "noOfActivities" : length(activities),
                    },
                    "competencies" : curriculumGrade[level._ref == $gradeId]{
                        "title": levelTitle,
                        levelOrder,
                        path,
                        globalId,
                        subdivisionLevel,
                        "competencyGoalCode" : competencyGoals,
                        ...(*[_type == "curriculumGrade" && _id == $gradeId ] {
                            ...(competencyGoals[level == ^.^.competencyGoals]){
                                subject,
                                topicId,
                                "competencyDescription": description,
                            }[0]
                        }[0]),
                    },
                },
    }`
}

export const curriculumGradeContentTopicListV2 = (): string => {
  return groq`
    *[
      _type == "curriculumGradeV2" &&
      countryCode == $countryCode &&
      gradeLevel == $gradeId
    ].gradeSubdivisions[]->{
        ...,
        "subjectOrder": subjectOrders[]->
      }
  `
}
